// =============================================================
// AttendanceHeatmap
// -------------------------------------------------------------
// Calendar-style heatmap for visualising per-day attendance state
// over a rolling 90-day window. Used by the Employee dashboard.
//
// Status mapping (controlled by the consumer):
//   present  → green
//   absent   → red
//   leave    → amber
//   weekend  → slate
//   holiday  → indigo
//   no_data  → light grey
//
// Layout: 7-row × 13-col grid. Each cell is a small rounded square
// with a hover tooltip that reveals the date and full state.
// =============================================================

import React, { useState } from 'react';

const COLOURS = {
  present:  '#22c55e',
  absent:   '#ef4444',
  leave:    '#f59e0b',
  weekend:  '#cbd5e1',
  holiday:  '#8b5cf6',
  no_data:  '#e2e8f0',
};

const STATUS_LABELS = {
  present:  'Present',
  absent:   'Absent',
  leave:    'On Leave',
  weekend:  'Weekend',
  holiday:  'Holiday',
  no_data:  'No data',
};

function fmtShort(d) {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

export default function AttendanceHeatmap({ days = [], onCellClick }) {
  const [hovered, setHovered] = useState(null);

  if (!days.length) {
    return (
      <div className="text-center text-muted py-3" style={{ fontSize: 12 }}>
        <i className="bi bi-calendar3" style={{ fontSize: 28, opacity: 0.25 }}></i>
        <div className="mt-2">No attendance data yet</div>
      </div>
    );
  }

  // Pad to start on Sunday so the grid is calendar-aligned
  const first = days[0].date;
  const lead = first.getDay();
  const cells = [...Array(lead).fill(null), ...days];

  // Group into weeks (rows of 7)
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: 4,
          marginBottom: 8,
        }}
      >
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} style={{ fontSize: 10, color: '#94a3b8', textAlign: 'center', fontWeight: 600 }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
        {weeks.flat().map((cell, i) => {
          if (!cell) return <div key={`empty-${i}`} />;
          const colour = COLOURS[cell.status] || COLOURS.no_data;
          return (
            <div
              key={cell.key}
              onClick={onCellClick ? () => onCellClick(cell) : undefined}
              onMouseEnter={() => setHovered({ ...cell, i })}
              onMouseLeave={() => setHovered(null)}
              style={{
                aspectRatio: '1 / 1',
                background: colour,
                borderRadius: 4,
                cursor: onCellClick ? 'pointer' : 'default',
                opacity: cell.status === 'no_data' ? 0.5 : 1,
                transition: 'transform .15s ease, box-shadow .15s ease',
                position: 'relative',
                transform: hovered?.i === i ? 'scale(1.18)' : 'scale(1)',
                boxShadow: hovered?.i === i ? `0 2px 8px ${colour}66` : 'none',
                zIndex: hovered?.i === i ? 2 : 1,
              }}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div className="d-flex flex-wrap gap-3 mt-3" style={{ fontSize: 11, color: '#475569' }}>
        {[
          { key: 'present', label: 'Present' },
          { key: 'leave',   label: 'Leave' },
          { key: 'absent',  label: 'Absent' },
          { key: 'weekend', label: 'Weekend' },
        ].map(l => (
          <div key={l.key} className="d-flex align-items-center gap-1">
            <span style={{
              width: 10, height: 10, borderRadius: 2,
              background: COLOURS[l.key],
            }}/>
            <span>{l.label}</span>
          </div>
        ))}
      </div>

      {/* Hover tooltip */}
      {hovered && (
        <div
          style={{
            marginTop: 8, padding: '6px 10px',
            background: '#0f172a', color: '#fff',
            fontSize: 11, borderRadius: 6, display: 'inline-block',
          }}
        >
          <strong>{fmtShort(hovered.date)}</strong> · {STATUS_LABELS[hovered.status] || hovered.status}
        </div>
      )}
    </div>
  );
}
